import { useEffect, useState } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { 
  FileText, Users, Vote, MapPin, TrendingUp, CheckCircle, Clock, AlertCircle, 
  ArrowRight, Plus, Key, Building2, Briefcase, HardHat, ListChecks, ChevronRight, ShieldCheck, HeartPulse, Lock
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Process flow steps
const PROCESS_STEPS = [
  { id: 1, title: "Society Formation", icon: Building2, path: "/setup", description: "Register society details and location" },
  { id: 2, title: "Pre-feasibility", icon: MapPin, path: "/prefeasibility", description: "Location-based viability check" },
  { id: 3, title: "Document Upload", icon: FileText, path: "/documents", description: "Upload property card, NOCs, etc." },
  { id: 4, title: "Feasibility Report", icon: TrendingUp, path: "/feasibility", description: "Detailed analysis with calculations" },
  { id: 5, title: "Add PMCs", icon: Briefcase, path: "/pmc", description: "Add Project Management Consultants" },
  { id: 6, title: "Add Builders", icon: HardHat, path: "/builders", description: "Add developers for bidding" },
  { id: 7, title: "Member Consent", icon: Users, path: "/builders", description: "Obtain 51% EOA consent" },
  { id: 8, title: "Developer Voting", icon: Vote, path: "/voting", description: "Members vote for developer" },
  { id: 9, title: "79A Process", icon: ListChecks, path: "/process79a", description: "Complete 12-step 79A process" },
];

export const Dashboard = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [society, setSociety] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [process79a, setProcess79a] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processStatus, setProcessStatus] = useState({});
  const [compliance, setCompliance] = useState(null);
  const [maxConsent, setMaxConsent] = useState(0);
  const [savingRole, setSavingRole] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [requestingVerification, setRequestingVerification] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [proofType, setProofType] = useState("registration_certificate");
  const [consentSubmitting, setConsentSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch societies
      const socRes = await fetch(`${API}/societies`, { credentials: "include" });
      const societies = await socRes.json();
      
      if (societies.length > 0) {
        const currentSociety = societies[0];
        setSociety(currentSociety);
        
        // Fetch readiness and 79A process
        const [readinessRes, processRes, docsRes, pmcRes, buildersRes, compRes] = await Promise.all([
          fetch(`${API}/societies/${currentSociety.society_id}/readiness`, { credentials: "include" }),
          fetch(`${API}/societies/${currentSociety.society_id}/process79a`, { credentials: "include" }),
          fetch(`${API}/societies/${currentSociety.society_id}/documents`, { credentials: "include" }),
          fetch(`${API}/societies/${currentSociety.society_id}/pmc`, { credentials: "include" }),
          fetch(`${API}/societies/${currentSociety.society_id}/builders`, { credentials: "include" }),
          fetch(`${API}/societies/${currentSociety.society_id}/compliance-check`, { credentials: "include" })
        ]);
        
        if (readinessRes.ok) setReadiness(await readinessRes.json());
        if (processRes.ok) setProcess79a(await processRes.json());
        if (compRes.ok) {
          const compData = await compRes.json();
          if (compData.report) setCompliance(compData);
        }
        
        // Calculate process status
        const docs = docsRes.ok ? await docsRes.json() : [];
        const pmcs = pmcRes.ok ? await pmcRes.json() : [];
        const builders = buildersRes.ok ? await buildersRes.json() : [];
        
        setMaxConsent(builders.reduce((m, b) => Math.max(m, b.consent_percentage || 0), 0));
        
        setProcessStatus({
          1: true, // Society created
          2: currentSociety.prefeasibility_done,
          3: docs.length > 0,
          4: currentSociety.readiness_score > 50,
          5: pmcs.length > 0,
          6: builders.length > 0,
          7: builders.some(b => b.consent_percentage >= 51),
          8: false, // TODO: Check if voting done
          9: false  // TODO: Check 79A progress
        });
      }
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getCurrentStep = () => {
    for (let i = 1; i <= 9; i++) {
      if (!processStatus[i]) return i;
    }
    return 9;
  };

  const selectRole = async (role) => {
    setSavingRole(true);
    try {
      await fetch(`${API}/auth/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
        credentials: "include"
      });
    } catch {
      // non-blocking
    } finally {
      setSavingRole(false);
      navigate("/setup");
    }
  };

  const requestVerification = async () => {
    if (!society) return;
    if (!proofFile) { toast.error("Please upload a proof document to claim this society"); return; }
    setRequestingVerification(true);
    try {
      const fd = new FormData();
      fd.append("proof_type", proofType);
      fd.append("file", proofFile);
      const res = await fetch(`${API}/societies/${society.society_id}/request-verification`, {
        method: "POST",
        body: fd,
        credentials: "include"
      });
      const result = await res.json();
      if (res.ok) {
        toast.success(result.message || "Verification requested");
        setVerifyDialogOpen(false);
        setProofFile(null);
        fetchData();
      } else {
        toast.error(result.detail || "Failed to request verification");
      }
    } catch {
      toast.error("Failed to request verification");
    } finally {
      setRequestingVerification(false);
    }
  };

  const acceptConsent = async () => {
    setConsentSubmitting(true);
    try {
      await fetch(`${API}/auth/consent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ version: "v1" }),
        credentials: "include"
      });
      await checkAuth();
    } catch {
      toast.error("Failed to record consent");
    } finally {
      setConsentSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (user && user.consent_given === false) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto py-12" data-testid="consent-screen">
          <Card>
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6 text-emerald-700" />
                </div>
                <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  Your privacy & consent
                </h2>
              </div>
              <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
                <p>Before you continue, please review how ReDevelop handles your data (as per India's DPDPA):</p>
                <ul className="list-disc pl-5 space-y-1.5 text-slate-600">
                  <li><strong>What we collect:</strong> your name, email/phone, society details, and documents you choose to upload.</li>
                  <li><strong>How it's used:</strong> to provide pre-feasibility, document storage, verification and redevelopment services for your society.</li>
                  <li><strong>Separation:</strong> your society's building-asset data is stored separately from personal resident data.</li>
                  <li><strong>Your control:</strong> you can request access or deletion of your personal data at any time.</li>
                </ul>
                <p className="text-xs text-slate-500">By continuing you consent to this processing. Your consent is recorded with a timestamp.</p>
              </div>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={acceptConsent}
                  disabled={consentSubmitting}
                  className="bg-emerald-700 hover:bg-emerald-800"
                  data-testid="consent-accept-btn"
                >
                  {consentSubmitting ? "Saving..." : "I Agree & Continue"}
                </Button>
                <Button variant="outline" onClick={() => window.location.href = "/"} data-testid="consent-decline-btn">
                  Not now
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (!society) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto py-14">
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <Building2 className="w-8 h-8 text-emerald-700" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
              Welcome to ReDevelop, {(user?.name?.trim()?.split(' ')[0]) || user?.email?.split('@')[0] || 'there'}!
            </h2>
            <p className="text-slate-600">
              Tell us your role in the society to get started. You'll get a free pre-feasibility check right away.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-sm font-medium" data-testid="free-check-badge">
              <CheckCircle className="w-4 h-4" /> Free pre-feasibility check — no payment needed
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <button
              onClick={() => selectRole("office_bearer")}
              disabled={savingRole}
              data-testid="role-office-bearer-btn"
              className="text-left p-6 rounded-2xl border-2 border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/50 transition-all group disabled:opacity-60"
            >
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-4 group-hover:bg-emerald-600 transition-colors">
                <Briefcase className="w-6 h-6 text-emerald-700 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope' }}>
                Register as Office Bearer
              </h3>
              <p className="text-sm text-slate-600">Chairman, Secretary or Treasurer managing the society's redevelopment.</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-emerald-700">Get started <ArrowRight className="w-4 h-4" /></span>
            </button>

            <button
              onClick={() => selectRole("member")}
              disabled={savingRole}
              data-testid="role-member-btn"
              className="text-left p-6 rounded-2xl border-2 border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/50 transition-all group disabled:opacity-60"
            >
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-colors">
                <Users className="w-6 h-6 text-blue-700 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope' }}>
                Register as Member
              </h3>
              <p className="text-sm text-slate-600">A society member who wants to add and track their society.</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-emerald-700">Get started <ArrowRight className="w-4 h-4" /></span>
            </button>
          </div>

          {savingRole && (
            <div className="flex items-center justify-center gap-2 text-slate-500 mt-6 text-sm">
              <Clock className="w-4 h-4 animate-spin" /> Setting up your registration...
            </div>
          )}

          <div className="mt-8 rounded-2xl border-2 border-emerald-200 bg-emerald-50/50 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="claim-code-block">
            <div>
              <h4 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Invited by your office bearers?</h4>
              <p className="text-sm text-slate-600">If you have a claim code, use it to join your society directly.</p>
            </div>
            <Button
              onClick={() => navigate("/claim")}
              variant="outline"
              className="border-emerald-600 text-emerald-700 hover:bg-emerald-100 flex-shrink-0"
              data-testid="claim-society-btn"
            >
              <Key className="w-4 h-4 mr-2" /> Claim with a code
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const completedSteps = process79a?.steps?.filter(s => s.status === "completed").length || 0;
  const totalSteps = process79a?.steps?.length || 12;
  const processProgress = Math.round((completedSteps / totalSteps) * 100);

  const quickActions = [
    { icon: FileText, label: "Upload Documents", path: "/documents", color: "bg-blue-100 text-blue-700" },
    { icon: Users, label: "Manage Members", path: "/members", color: "bg-purple-100 text-purple-700" },
    { icon: Vote, label: "Developer Voting", path: "/voting", color: "bg-orange-100 text-orange-700" },
    { icon: MapPin, label: "Pre-feasibility", path: "/prefeasibility", color: "bg-emerald-100 text-emerald-700" }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              {society.name}
            </h1>
            <p className="text-slate-600">{society.address}</p>
            {/* Verification status */}
            {society.verification_status === "verified" || !society.verification_status ? (
              society.is_claimed ? (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-600 mt-1">
                  <CheckCircle className="w-3 h-3" /> Society Claimed
                </span>
              ) : null
            ) : society.verification_status === "pending_verification" ? (
              <span className="inline-flex items-center gap-1 text-xs text-amber-600 mt-1" data-testid="verification-status-pending">
                <Clock className="w-3 h-3" /> Verification pending
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-slate-500 mt-1" data-testid="verification-status-unverified">
                <Lock className="w-3 h-3" /> Not yet verified
              </span>
            )}
          </div>
          <Button 
            onClick={() => navigate("/feasibility")}
            data-testid="view-feasibility-btn"
            className="bg-emerald-700 hover:bg-emerald-800"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            View Feasibility Report
          </Button>
        </div>

        {/* Verification / Claim CTA */}
        {(society.verification_status === "unverified" || society.verification_status === "rejected") && (
          <Card className="border-amber-300 bg-amber-50" data-testid="claim-society-banner">
            <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Lock className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  Unlock Document Vault & advanced services
                </h3>
                <p className="text-sm text-slate-600">
                  Your free pre-feasibility is ready. Claim this society to unlock document uploads, feasibility reports, members, voting and more after verification from our team.
                </p>
              </div>
              <Button
                onClick={() => setVerifyDialogOpen(true)}
                className="bg-emerald-700 hover:bg-emerald-800 flex-shrink-0"
                data-testid="claim-this-society-btn"
              >
                <ShieldCheck className="w-4 h-4 mr-2" /> Claim this Society
              </Button>
            </CardContent>
          </Card>
        )}
        {society.verification_status === "pending_verification" && (
          <Card className="border-amber-300 bg-amber-50" data-testid="verification-pending-banner">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  Pending Verification
                </h3>
                <p className="text-sm text-slate-600">
                  Our team will connect with you soon to unlock the possibilities for your society.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Claim confirmation dialog */}
        <AlertDialog open={verifyDialogOpen} onOpenChange={setVerifyDialogOpen}>
          <AlertDialogContent data-testid="claim-society-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Claim this Society</AlertDialogTitle>
              <AlertDialogDescription>
                You can claim this society to unlock document uploads and further services after verification from our team. Please upload proof that you are an office bearer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="text-sm font-medium text-slate-700">Proof type</label>
                <select
                  value={proofType}
                  onChange={(e) => setProofType(e.target.value)}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                  data-testid="proof-type-select"
                >
                  <option value="registration_certificate">Society Registration Certificate</option>
                  <option value="agm_notice">Latest AGM Notice</option>
                  <option value="committee_letterhead">Managing Committee Letterhead (naming you)</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Upload proof (PDF or image)</label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                  className="mt-1 w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-emerald-100 file:text-emerald-700"
                  data-testid="proof-file-input"
                />
                {proofFile && <p className="text-xs text-emerald-600 mt-1">Selected: {proofFile.name}</p>}
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={requestingVerification}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); requestVerification(); }}
                disabled={requestingVerification || !proofFile}
                className="bg-emerald-700 hover:bg-emerald-800"
                data-testid="confirm-claim-btn"
              >
                {requestingVerification ? "Submitting..." : "Confirm & Request Verification"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Redevelopment Health Card */}
        <Card className="overflow-hidden border-emerald-200" data-testid="health-card">
          <CardHeader className="bg-gradient-to-r from-emerald-700 to-emerald-600 py-4">
            <CardTitle className="flex items-center gap-2 text-white text-lg">
              <HeartPulse className="w-5 h-5" />
              Redevelopment Health Card
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div
                className="p-3 rounded-lg bg-emerald-50 border border-emerald-100 cursor-pointer hover:border-emerald-300 transition-colors"
                onClick={() => navigate("/documents")}
                data-testid="health-readiness"
              >
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                  <TrendingUp className="w-3.5 h-3.5" /> Readiness
                </div>
                <p className="text-2xl font-bold text-emerald-700" style={{ fontFamily: 'Manrope' }}>
                  {readiness?.score || 0}%
                </p>
                <p className="text-xs text-slate-500">Documents & data</p>
              </div>

              <div
                className="p-3 rounded-lg bg-blue-50 border border-blue-100 cursor-pointer hover:border-blue-300 transition-colors"
                onClick={() => navigate("/prefeasibility")}
                data-testid="health-prefeasibility"
              >
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                  <MapPin className="w-3.5 h-3.5" /> Pre-feasibility
                </div>
                <p className={`text-lg font-bold ${society.prefeasibility_done ? (society.prefeasibility_viable ? 'text-emerald-600' : 'text-amber-600') : 'text-slate-400'}`} style={{ fontFamily: 'Manrope' }}>
                  {society.prefeasibility_done ? (society.prefeasibility_viable ? "Viable" : "Review") : "Pending"}
                </p>
                <p className="text-xs text-slate-500">Location check</p>
              </div>

              <div
                className="p-3 rounded-lg bg-purple-50 border border-purple-100 cursor-pointer hover:border-purple-300 transition-colors"
                onClick={() => navigate("/feasibility")}
                data-testid="health-compliance"
              >
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                  <ShieldCheck className="w-3.5 h-3.5" /> Compliance
                </div>
                <p className={`text-lg font-bold ${compliance ? 'text-emerald-600' : 'text-slate-400'}`} style={{ fontFamily: 'Manrope' }}>
                  {compliance ? "Checked" : "Pending"}
                </p>
                <p className="text-xs text-slate-500">{compliance?.regulation || "DCPR / UDCPR"}</p>
              </div>

              <div
                className="p-3 rounded-lg bg-orange-50 border border-orange-100 cursor-pointer hover:border-orange-300 transition-colors"
                onClick={() => navigate("/builders")}
                data-testid="health-consent"
              >
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                  <Users className="w-3.5 h-3.5" /> Member Consent
                </div>
                <p className={`text-2xl font-bold ${maxConsent >= 51 ? 'text-emerald-600' : 'text-orange-600'}`} style={{ fontFamily: 'Manrope' }}>
                  {Math.round(maxConsent)}%
                </p>
                <p className="text-xs text-slate-500">{maxConsent >= 51 ? "51% threshold met" : "Target: 51%"}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Process Flow - Visual Sequence */}
        <Card className="overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-emerald-50 to-blue-50 border-b">
            <CardTitle className="flex items-center gap-2">
              <ListChecks className="w-5 h-5 text-emerald-700" />
              Redevelopment Process Flow
            </CardTitle>
            <p className="text-sm text-slate-600">Step-by-step guide from formation to completion</p>
          </CardHeader>
          <CardContent className="p-4">
            <div className="relative">
              {/* Progress Line */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200" />
              <div 
                className="absolute left-6 top-0 w-0.5 bg-emerald-500 transition-all duration-500"
                style={{ height: `${(getCurrentStep() / 9) * 100}%` }}
              />
              
              <div className="space-y-3">
                {PROCESS_STEPS.map((step, idx) => {
                  const isCompleted = processStatus[step.id];
                  const isCurrent = getCurrentStep() === step.id;
                  const Icon = step.icon;
                  
                  return (
                    <div 
                      key={step.id}
                      className={`relative flex items-center gap-4 p-3 rounded-lg cursor-pointer transition-all
                        ${isCurrent ? 'bg-emerald-50 border border-emerald-200' : 'hover:bg-slate-50'}
                        ${isCompleted ? 'opacity-100' : 'opacity-70'}`}
                      onClick={() => navigate(step.path)}
                    >
                      {/* Step Circle */}
                      <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0
                        ${isCompleted ? 'bg-emerald-500 text-white' : isCurrent ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500' : 'bg-slate-100 text-slate-400'}`}
                      >
                        {isCompleted ? (
                          <CheckCircle className="w-6 h-6" />
                        ) : (
                          <Icon className="w-5 h-5" />
                        )}
                      </div>
                      
                      {/* Step Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${isCompleted ? 'text-emerald-700' : isCurrent ? 'text-slate-900' : 'text-slate-600'}`}>
                            {step.id}. {step.title}
                          </span>
                          {isCurrent && (
                            <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full">
                              Current
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 truncate">{step.description}</p>
                      </div>
                      
                      {/* Arrow */}
                      <ChevronRight className={`w-5 h-5 flex-shrink-0 ${isCurrent ? 'text-emerald-600' : 'text-slate-300'}`} />
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bento Grid */}
        <div className="bento-grid">
          {/* Readiness Score - Large Card */}
          <Card className="bento-card bento-large" data-testid="readiness-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-slate-700">Readiness Score</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center py-4">
                <div className="gauge-container">
                  <svg viewBox="0 0 200 200" className="w-full h-full">
                    <circle
                      cx="100" cy="100" r="80"
                      className="gauge-bg"
                    />
                    <circle
                      cx="100" cy="100" r="80"
                      className="gauge-fill progress-ring"
                      strokeDasharray={`${2 * Math.PI * 80}`}
                      strokeDashoffset={`${2 * Math.PI * 80 * (1 - (readiness?.score || 0) / 100)}`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center flex-col">
                    <span className="text-5xl font-bold text-emerald-700" style={{ fontFamily: 'Manrope' }}>
                      {readiness?.score || 0}%
                    </span>
                    <span className="text-sm text-slate-500">Complete</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {readiness?.checklist?.slice(0, 4).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    {item.uploaded ? (
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                    )}
                    <span className={item.uploaded ? "text-slate-700" : "text-slate-500"}>
                      {item.name}
                    </span>
                  </div>
                ))}
              </div>
              <Button 
                variant="outline" 
                className="w-full mt-4"
                onClick={() => navigate("/documents")}
                data-testid="upload-docs-btn"
              >
                Upload Missing Documents
              </Button>
            </CardContent>
          </Card>

          {/* 79A Process Tracker - Tall Card */}
          <Card className="bento-card bento-tall" data-testid="process-tracker-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-slate-700">79A Process</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Progress</span>
                  <span className="font-medium text-emerald-700">{processProgress}%</span>
                </div>
                <Progress value={processProgress} className="h-2" />
              </div>
              <div className="space-y-1 max-h-[280px] overflow-y-auto pr-2">
                {process79a?.steps?.slice(0, 6).map((step, idx) => (
                  <div key={idx} className="step-item">
                    <div className={`step-indicator ${
                      step.status === "completed" ? "step-completed" :
                      step.status === "in_progress" ? "step-active" : "step-pending"
                    }`}>
                      {step.status === "completed" ? (
                        <CheckCircle className="w-3 h-3" />
                      ) : (
                        step.step
                      )}
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${
                        step.status === "completed" ? "text-slate-900" : "text-slate-600"
                      }`}>
                        {step.title}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <Button 
                variant="outline" 
                className="w-full mt-4"
                onClick={() => navigate("/process79a")}
                data-testid="view-process-btn"
              >
                View Full Process
              </Button>
            </CardContent>
          </Card>

          {/* Society Stats - Wide Card */}
          <Card className="bento-card bento-wide" data-testid="society-stats-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-slate-700">Society Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                    {society.total_flats || 0}
                  </div>
                  <div className="text-xs text-slate-500">Total Flats</div>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                    {society.total_members || 0}
                  </div>
                  <div className="text-xs text-slate-500">Members</div>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                    {society.plot_area || '-'}
                  </div>
                  <div className="text-xs text-slate-500">Plot Area (sq.m)</div>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                    {society.year_built || '-'}
                  </div>
                  <div className="text-xs text-slate-500">Year Built</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          {quickActions.map((action, idx) => (
            <Card 
              key={idx} 
              className="bento-card cursor-pointer hover:border-emerald-300"
              onClick={() => navigate(action.path)}
              data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${action.color}`}>
                  <action.icon className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">{action.label}</p>
                  <p className="text-xs text-slate-500">Click to open</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};
